import { useState, useEffect, useCallback, useMemo } from "react";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import NapCodeMapLinks from "../components/common/NapCodeMapLinks";
import NapDetailsResultModal from "../components/modals/NapDetailsResultModal";
import NapProspectsModal from "../components/modals/NapProspectsModal";
import NapPdsMatchModal from "../components/modals/NapPdsMatchModal";

const normNap = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, " ");

/* ---------- helpers ---------- */

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatDayHeader(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-PH", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function VacantCell({ value }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  return (
    <span className={`font-semibold ${value > 0 ? "text-green-700" : "text-red-600"}`}>
      {value}
    </span>
  );
}

function TypeBadge({ type }) {
  const ni = type === "NI";
  return (
    <span
      className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${
        ni ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
      }`}
    >
      {type}
    </span>
  );
}

/* ---------- Main page ---------- */

export default function NapUsagePage() {
  const isMobile = useIsMobile();
  const { hasPerm } = useAuth();
  // Fetching NAP details needs page access (to resolve the record by code) and
  // the qr_generate permission (which the POST /nap-details endpoint requires).
  const canViewNapDetails = hasPerm("page.nap_locator") && hasPerm("nap_locator.qr_generate");
  // Clicking Actual Vacant Ports → nearby non-completed prospects (admin-gated).
  const canViewProspects = hasPerm("reports.nap_usage_prospects");
  // Clicking Subscriber Name → disconnected (PDS/ADS) subs nearby (admin-gated).
  const canViewPdsMatch = hasPerm("reports.nap_usage_pds_match");

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [search, setSearch] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // NAP Details modal (opened from the System Vacant Ports column)
  const [napDetails, setNapDetails] = useState(null);
  const [detailsLoadingCode, setDetailsLoadingCode] = useState(null);
  const [detailsError, setDetailsError] = useState("");

  // Nearby Prospects modal (opened from the Actual Vacant Ports column)
  const [prospects, setProspects] = useState(null); // { napCode, data }
  const [prospectsLoading, setProspectsLoading] = useState(false);

  // PDS/ADS match modal (opened from the Subscriber Name column)
  const [pdsMatch, setPdsMatch] = useState(null); // { name, data }
  const [pdsMatchLoading, setPdsMatchLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/nap-usage", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/reports/nap-usage/export", {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Daily_NAP_Usage_${dateFrom}_${dateTo}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // handled by interceptor
    } finally {
      setExporting(false);
    }
  };

  // Client-side filter by NAP code, subscriber name, or address.
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        (r.nap_code || "").toLowerCase().includes(q) ||
        (r.subs_name || "").toLowerCase().includes(q) ||
        (r.address || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  // Resolve the NAP Locator record by code, then fetch & show its NAP details.
  const openNapDetails = async (napCode) => {
    if (!napCode || detailsLoadingCode) return;
    setDetailsError("");
    setDetailsLoadingCode(napCode);
    try {
      const { data: list } = await api.get("/nap-locator", {
        params: { search: napCode, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" },
      });
      const match = (Array.isArray(list) ? list : []).find((o) => normNap(o.nap_code) === normNap(napCode));
      if (!match) {
        setDetailsError(`NAP code "${napCode}" was not found in the NAP Locator.`);
        return;
      }
      const { data: details } = await api.post(`/nap-locator/${match.id}/nap-details`);
      if (details.found) {
        setNapDetails({ ...details, _recordId: match.id });
      } else {
        setDetailsError(details.detail || `No NAP details found for "${napCode}".`);
      }
    } catch (err) {
      setDetailsError(err.response?.data?.detail || "Failed to fetch NAP details from the external server.");
    } finally {
      setDetailsLoadingCode(null);
    }
  };

  // Find nearby non-completed NI/MIG subscribers for the clicked installed sub.
  const openProspects = async (row) => {
    if (!row || prospectsLoading) return;
    setProspects({ napCode: row.nap_code, data: null });
    setProspectsLoading(true);
    try {
      const { data } = await api.get("/reports/nap-usage/prospects", {
        params: {
          municipality: row.municipality || "",
          barangay: row.barangay || "",
          address: row.address || "",
          nap_code: row.nap_code || "",
        },
      });
      setProspects({ napCode: row.nap_code, data });
    } catch {
      setProspects(null);
    } finally {
      setProspectsLoading(false);
    }
  };

  // Find disconnected (PDS/ADS) subscribers near the clicked installed sub.
  const openPdsMatch = async (row) => {
    if (!row || pdsMatchLoading) return;
    setPdsMatch({ name: row.subs_name, data: null });
    setPdsMatchLoading(true);
    try {
      const { data } = await api.get("/reports/nap-usage/pds-match", {
        params: {
          municipality: row.municipality || "",
          barangay: row.barangay || "",
          address: row.address || "",
        },
      });
      setPdsMatch({ name: row.subs_name, data });
    } catch {
      setPdsMatch(null);
    } finally {
      setPdsMatchLoading(false);
    }
  };

  const tdCls = "px-3 py-2 text-gray-700 align-top";

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <a href="/reports" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </a>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Daily NAP Usage</h1>
        <p className="text-sm text-gray-500 mb-4">
          NAP codes used by completed New Installation & Migration jobs, with the field team's
          physical-inspection vacant-port reading. Use it to spot NAPs that still have free ports.
        </p>

        {/* ---- Filters ---- */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="NAP code, subscriber, or address…"
              className="border rounded px-3 py-1.5 text-sm w-full"
            />
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || rows.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
        </div>

        {/* ---- Summary ---- */}
        {!loading && data && (
          <div className="mb-3 text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
            <span><strong className="text-gray-800">{data.summary.total_subs}</strong> subscriber(s) connected</span>
            <span><strong className="text-gray-800">{data.summary.total_nap_days}</strong> NAP-day(s) used</span>
            {search.trim() && (
              <span><strong className="text-gray-800">{rows.length}</strong> matching row(s)</span>
            )}
          </div>
        )}

        {detailsError && (
          <div className="mb-3 flex items-start justify-between gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2">
            <span>{detailsError}</span>
            <button onClick={() => setDetailsError("")} className="text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading && <div className="text-center text-gray-500 py-8">Loading report…</div>}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400">
            No NAP usage for the selected filters.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-14">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-28">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[220px]">NAP Code</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-20">NI/MIG</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-28">
                    System Vacant Ports<br /><span className="font-normal text-[11px] text-gray-400">(per NAP Locator)</span>
                  </th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-28">
                    Actual Vacant Ports<br /><span className="font-normal text-[11px] text-gray-400">(field inspection)</span>
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[180px]">Subscriber Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[240px]">Address</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const prev = rows[idx - 1];
                  const newDay = !prev || prev.date !== r.date;
                  return (
                    <tr
                      key={`${r.date}-${r.nap_code}-${r.type}-${r.subs_name}-${idx}`}
                      className={`${newDay ? "border-t-4 border-amber-200" : "border-t border-gray-100"} ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td className="px-3 py-2 text-center font-bold text-gray-800 align-top">{idx + 1}</td>
                      <td className={tdCls}>
                        {newDay ? (
                          <span className="font-semibold text-gray-800">{formatDayHeader(r.date)}</span>
                        ) : (
                          <span className="text-gray-400">{r.date}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <NapCodeMapLinks napCode={r.nap_code} />
                      </td>
                      <td className="px-3 py-2 text-center align-top"><TypeBadge type={r.type} /></td>
                      <td className="px-3 py-2 text-center align-top">
                        {r.system_vacant != null && canViewNapDetails ? (
                          <button
                            type="button"
                            onClick={() => openNapDetails(r.nap_code)}
                            disabled={!!detailsLoadingCode}
                            title="View NAP details"
                            className={`font-semibold underline decoration-dotted hover:text-blue-700 disabled:opacity-50 ${
                              r.system_vacant > 0 ? "text-green-700" : "text-red-600"
                            }`}
                          >
                            {detailsLoadingCode === r.nap_code ? "…" : r.system_vacant}
                          </button>
                        ) : (
                          <VacantCell value={r.system_vacant} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-center align-top">
                        {r.actual_vacant != null && canViewProspects ? (
                          <button
                            type="button"
                            onClick={() => openProspects(r)}
                            disabled={prospectsLoading}
                            title="Find nearby non-completed subscribers for this NAP"
                            className={`font-semibold underline decoration-dotted hover:text-blue-700 disabled:opacity-50 ${
                              r.actual_vacant > 0 ? "text-green-700" : "text-red-600"
                            }`}
                          >
                            {r.actual_vacant}
                          </button>
                        ) : (
                          <VacantCell value={r.actual_vacant} />
                        )}
                      </td>
                      <td className={tdCls}>
                        {r.subs_name && canViewPdsMatch ? (
                          <button
                            type="button"
                            onClick={() => openPdsMatch(r)}
                            disabled={pdsMatchLoading}
                            title="Find disconnected (PDS/ADS) subscribers nearby"
                            className="text-left font-medium text-blue-700 underline decoration-dotted hover:text-blue-900 disabled:opacity-50"
                          >
                            {r.subs_name}
                          </button>
                        ) : (
                          r.subs_name || "—"
                        )}
                      </td>
                      <td className={tdCls}>{r.address || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loading overlay while resolving / fetching NAP details */}
      {detailsLoadingCode && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg text-sm text-gray-700">
            Fetching NAP details for <strong>{detailsLoadingCode}</strong>…
          </div>
        </div>
      )}

      {/* NAP Details modal — same view shown from the NAP Locator page */}
      {napDetails && (
        <NapDetailsResultModal
          data={napDetails}
          onClose={() => setNapDetails(null)}
          onRefresh={napDetails._recordId ? async () => {
            const { data: refreshed } = await api.post(`/nap-locator/${napDetails._recordId}/nap-details?force=true`);
            if (refreshed.found) {
              setNapDetails({ ...refreshed, _recordId: napDetails._recordId });
            } else {
              setNapDetails(null);
              setDetailsError(refreshed.detail || "No NAP details found on the external server.");
            }
          } : undefined}
        />
      )}

      {/* Nearby Prospects modal — non-completed subs on the same street/brgy/mun */}
      {prospects && (
        <NapProspectsModal
          napCode={prospects.napCode}
          loading={prospectsLoading}
          data={prospects.data}
          onClose={() => setProspects(null)}
        />
      )}

      {/* PDS/ADS match modal — disconnected subs on the same street/brgy/mun */}
      {pdsMatch && (
        <NapPdsMatchModal
          subscriberName={pdsMatch.name}
          loading={pdsMatchLoading}
          data={pdsMatch.data}
          onClose={() => setPdsMatch(null)}
        />
      )}
    </div>
  );
}
